import React, { Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';

import AlbumFolderChildren from '../global/album-folders/AlbumFolderChildren';
import Button from '../global/Button';
import Spinner from '../global/Spinner';
import IdentityNavModal from './IdentityNavModal';
import {
  DISCOVER_IDENTITY_NAV_MODAL,
  DISCOVER_IDENTITY_SELECTED_ALBUMS,
  DISCOVER_PURCHASE_SELECTION_MODAL,
} from '../../constants/modals';
import { isPrimaryIdentity } from '../../util/identity';
import DiscoverAlbumFolderNavigationContainer from '../../containers/discover/DiscoverAlbumFolderNavigationContainer';
import DiscoverAlbumFolderAlbumSelectionModal from './DiscoverAlbumFolderAlbumSelectionModal';
import AlbumSelectionButton from './AlbumSelectionButton';
import DiscoverAlbumFolderBrowseContainer from '../../containers/discover/DiscoverAlbumFolderBrowseContainer';
import OpenFolderIcon from '../icons/OpenFolderIcon';
import {
  albumFolderIsRoot,
  folderHasSubFolders,
  isPrepaidOnlyAlbumFolder,
} from '../../util/album-folder';
import {
  getSelectedAlbumData,
  hasSelectedAlbums,
} from '../../util/discover-album-folder';
import DiscoverAlbumFolderJoinTypeSelectionModal from '../../containers/discover/DiscoverAlbumFolderJoinTypeSelectionModal';
import {
  isAlbumFolderTagSelectionRequired,
  isAlbumFolderGradeTagSelectionRequired,
} from '../../apps/temp/views/onboarding/TagSelection/helpers';
import IdentityTagSelectionModal, {
  IDENTITY_TAG_SELECTION_MODAL,
} from '../../apps/temp/views/onboarding/TagSelection/components/IdentityTagSelection/modal';
import { ALBUM_TYPE } from '../../constants/albums';
import GradeTagSelectionModal, {
  GRADE_TAG_SELECTION_MODAL,
} from '../../apps/temp/views/onboarding/TagSelection/components/GradeTagSelection/modal';
import WaldoSpeechBubble from '../global/WaldoSpeechBubble';
import AngleLeftIcon from '../icons/AngleLeftIcon';
import AlbumFolderGalleryIcon from '../icons/AlbumFolderGalleryIcon';
import { sentryCaptureMessage } from '../../util/sentry';
import OnboardingLayoutFooter from '../../apps/temp/views/onboarding/OnboardingLayout/components/OnboardingLayoutFooter';

const propTypes = {
  account: PropTypes.shape({
    id: PropTypes.string,
  }),
  albumFolder: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    discovery_selection_info: PropTypes.string,
    individual_albums: PropTypes.bool,
  }),
  onSelectAlbum: PropTypes.func.isRequired,
  onDeselectAlbum: PropTypes.func.isRequired,
  onSelectAlbumCustomPrint: PropTypes.func.isRequired,
  onDeselectAlbumCustomPrint: PropTypes.func.isRequired,
  onResetIdentity: PropTypes.func.isRequired,
  identity: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    selectedAlbums: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
      }),
    ),
  }).isRequired,
  albumFolderId: PropTypes.string.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  openModal: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  onSelectIdentity: PropTypes.func.isRequired,
  allowsJoinTypeSelection: PropTypes.bool,
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }),
  selectedIdentities: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
    }),
  ),
  selectedIdentityId: PropTypes.string,
};

const DiscoverAlbumFolder = ({
  albumFolder,
  onSelectAlbum,
  onDeselectAlbum,
  onSelectAlbumCustomPrint,
  onDeselectAlbumCustomPrint,
  onSelectIdentity,
  identity,
  albumFolderId,
  openModal,
  closeModal,
  loading,
  account,
  location,
  history,
  onResetIdentity,
  selectedIdentities,
  selectedIdentityId,

  // `allowsJoinTypeSelection` is initially set in `SetAlbumFolderJoinPreferences`
  // via the redux action (based on album folder config and other params)
  // when the onboarding initializes and then can be set again
  // by editing the identity during the checkout summary.
  allowsJoinTypeSelection,
}) => {
  const switchIdentity = () => {
    onResetIdentity();
    history.replace(`/discover/album-folder/${albumFolderId}/faces`);
  };

  const onCompleteEnrollment = () => {
    if (isPrepaidOnlyAlbumFolder(albumFolder?.root ?? albumFolder)) {
      history.replace({
        pathname: `/purchase/album-folder/${albumFolderId}/pre-purchase`,
        state: { fromDiscovery: true },
      });
    } else {
      history.replace(`/discover/album-folder/${albumFolderId}/summary`);
    }
  };

  const onIdentitySetupCompleted = () => {
    // Use this so we can easily add SUBTYPES to show the modal to, if needed.
    const identityModalSubtypes = [];

    if (identityModalSubtypes.includes(albumFolder.album_subtype)) {
      openModal(DISCOVER_IDENTITY_NAV_MODAL);
    } else {
      onCompleteEnrollment();
    }
  };

  const onSelectionsCompleted = () => {
    return allowsJoinTypeSelection
      ? openModal(DISCOVER_PURCHASE_SELECTION_MODAL)
      : onIdentitySetupCompleted();
  };

  useEffect(() => {
    if (!identity) {
      sentryCaptureMessage('[DiscoverAlbumFolder] - Identity not found', {
        level: 'warning',
        extra: {
          accountId: account.id,
          selectedIdentities,
          selectedIdentityId,
        },
      });
      switchIdentity();
    }
  }, [identity]);

  useEffect(() => {
    if (!albumFolder || !identity) {
      return;
    }

    // Handle the individual album case on mount where we want to
    // immediately select the album behind the scenes and return to the
    // faces screen (or deselect the album if it's selected).
    if (albumFolder.individual_albums) {
      const { selectedAlbums } = identity;
      const [album] = albumFolder.children.albums.records;

      if (!selectedAlbums.length) {
        onSelectAlbum(getSelectedAlbumData(album, albumFolder));
        onSelectionsCompleted();
      } else {
        onDeselectAlbum(album, identity.id);
        switchIdentity();
      }
    }
  }, [albumFolder]);

  if (!identity) {
    return null;
  }

  if (loading) {
    return (
      <div className="text-center">
        <Spinner />
      </div>
    );
  }

  const albumFolderAlbums = albumFolder.children.albums.records;
  const albumFolderSelectedAlbums = albumFolderAlbums.filter(
    album =>
      !!identity.selectedAlbums.find(
        selectedAlbum => selectedAlbum.id === album.id,
      ),
  );

  return (
    <Fragment>
      <WaldoSpeechBubble position="right" className="mb-4">
        Please tell me where to look for{' '}
        <span className="text-primary">
          {isPrimaryIdentity(identity) ? 'you' : identity.name}
        </span>
        !
      </WaldoSpeechBubble>

      <div className="w-100 mb-2">
        <Button
          btnStyle="link"
          className="text-bold"
          onClick={() => {
            if (albumFolderIsRoot(albumFolder)) {
              history.push(`/discover/album-folder/${albumFolder.id}/faces`);
            } else {
              history.push(
                `/discover/album-folder/${albumFolder.parent.id}/select`,
              );
            }
          }}
        >
          <AngleLeftIcon className="mr-1" color="#0d84fe" height={14} /> Back
        </Button>
      </div>

      {(folderHasSubFolders(albumFolder) ||
        !albumFolderIsRoot(albumFolder)) && (
        <Fragment>
          <div className="w-100 d-flex justify-content-between align-items-center mb-2">
            {albumFolderIsRoot(albumFolder) ? (
              <h3 className="text-primary text-truncate max-w-50p mb-0">
                <AlbumFolderGalleryIcon
                  color="#0d84fe"
                  className="mr-1"
                  size={16}
                />
                {albumFolder.name}
              </h3>
            ) : (
              <div className="d-flex justify-content-center">
                <DiscoverAlbumFolderNavigationContainer openModal={openModal} />
              </div>
            )}
            <div className="d-flex">
              <AlbumSelectionButton
                identity={identity}
                onClick={() => openModal(DISCOVER_IDENTITY_SELECTED_ALBUMS)}
                className="ml-auto"
              />
              <DiscoverAlbumFolderBrowseContainer
                toggleComponent={
                  <div className="d-flex align-items-center ml-2">
                    <OpenFolderIcon />
                    <span className="text-extra-bold text-primary d-none d-md-inline-block">
                      Browse All
                    </span>
                  </div>
                }
              />
            </div>
          </div>
        </Fragment>
      )}

      <DiscoverAlbumFolderAlbumSelectionModal
        identity={identity}
        onDeselectAlbum={onDeselectAlbum}
        albumFolder={albumFolder}
        openModal={openModal}
      />

      <AlbumFolderChildren
        albumFolder={albumFolder}
        basePath="/discover/album-folder"
        onSelectAlbum={onSelectAlbum}
        onDeselectAlbum={onDeselectAlbum}
        albumFolderSelectedAlbums={albumFolderSelectedAlbums}
        identity={identity}
        loading={loading}
        allowAllSelection={albumFolder.album_type === ALBUM_TYPE.SHUTTERBUG}
      />

      {hasSelectedAlbums([identity]) && !loading && (
        <OnboardingLayoutFooter>
          <Button
            btnSize="lg"
            btnBlock
            onClick={onSelectionsCompleted}
            className="mt-2"
            disabled={!hasSelectedAlbums([identity]) || loading}
          >
            Confirm Selections
          </Button>
        </OnboardingLayoutFooter>
      )}

      <DiscoverAlbumFolderJoinTypeSelectionModal
        albumFolder={albumFolder.root}
        identity={identity}
        onAfterSelect={() => {
          if (isAlbumFolderGradeTagSelectionRequired(albumFolder.root)) {
            openModal(GRADE_TAG_SELECTION_MODAL, {
              onComplete: () => {
                if (isAlbumFolderTagSelectionRequired(albumFolder.root)) {
                  openModal(IDENTITY_TAG_SELECTION_MODAL, {
                    onComplete: () => onIdentitySetupCompleted(),
                  });

                  return;
                }

                onIdentitySetupCompleted();
              },
            });

            return;
          }

          if (isAlbumFolderTagSelectionRequired(albumFolder.root)) {
            openModal(IDENTITY_TAG_SELECTION_MODAL, {
              onComplete: () => onIdentitySetupCompleted(),
            });

            return;
          }

          onIdentitySetupCompleted();
        }}
        onSelectIdentity={onSelectIdentity}
        onSelectAlbumCustomPrint={onSelectAlbumCustomPrint}
        onDeselectAlbumCustomPrint={onDeselectAlbumCustomPrint}
      />

      <IdentityNavModal
        identity={identity}
        albumFolder={albumFolder}
        switchIdentity={switchIdentity}
        albumFolderId={albumFolderId}
        closeModal={closeModal}
        onCompleteEnrollment={onCompleteEnrollment}
        account={account}
        location={location}
      />

      <IdentityTagSelectionModal
        identity={identity}
        albumFolder={albumFolder.root}
      />
      <GradeTagSelectionModal
        identity={identity}
        albumFolder={albumFolder.root}
      />
    </Fragment>
  );
};

DiscoverAlbumFolder.propTypes = propTypes;

export default DiscoverAlbumFolder;
